import { HttpClient, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateCacheModule, TranslateCacheService, TranslateCacheSettings } from 'ngx-translate-cache';
//import { stringify } from 'querystring';
import { environment } from 'src/environments/environment';
import { SharedService } from 'src/services/shared.service';
import { forkJoin } from 'rxjs';

@NgModule({
  imports: [
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    TranslateCacheModule.forRoot({
      cacheService: {
        provide: TranslateCacheService,
        useFactory: translateCacheFactory,
        deps: [TranslateService, TranslateCacheSettings]
      },
      cacheMechanism: 'Cookie'
    })
  ],
  exports: [TranslateModule]
})

export class I18nModule {

  constructor(
    translate: TranslateService,
    translateCacheService: TranslateCacheService,
    public sharedService: SharedService
  ) {
    // translateCacheService.init() is intentionally NOT called here.
    // init() reads the 'lang' cookie synchronously and immediately calls
    // translate.use(cachedLang) — which fires the authenticated translation API
    // before the OIDC token is available, causing a 401 on every cold start.
    // Language preference is persisted via CRMUserProfile (localStorage + server),
    // so the cookie cache is redundant. The header's ngOnInit drives language
    // selection after authentication completes.

    const originalGet = translate.get.bind(translate);

    translate.get = function (key: any, interpolateParams?: Object) {

      if (typeof key === 'string') {
        key = key.toLowerCase();
      }

      if (Array.isArray(key)) {
        key = key.map(k => k.toLowerCase());
      }

      return originalGet(key, interpolateParams);
    };


    this.sharedService.getLanguageCodes().subscribe(result => {
      localStorage.setItem("languageCodes", JSON.stringify(result));

      var languageCodes = JSON.parse(localStorage.getItem("languageCodes"));
      let languages = new Array<string>();
      for (let item of languageCodes) {
        languages.push(item.code);
      };
      translate.addLangs(languages);

      // Language activation is intentionally deferred to the authenticated header
      // component (desktop/mobile ngOnInit). Calling translate.use() here fires the
      // translation API before the JWT token is available (causing a 401), and the
      // async CDN response can arrive after the header has already set the user's
      // preferred language, silently overwriting it.
    });
    // re-fetch code-lists whenever language changes
    translate.onLangChange.subscribe((evt) => {
      const lang = evt.lang;
      forkJoin([
        this.sharedService.getCodeList('TargetMarket', lang),
        this.sharedService.getCodeList('UnitOfMesure', lang),
        this.sharedService.geLicenceTypes(),
        this.sharedService.getCodeList('LocationRole', lang),
        this.sharedService.getCodeList('OrganizationRole', lang),
        this.sharedService.getCodeList('OrganizationID', lang),
        this.sharedService.getCodeList('LocationID', lang),
        this.sharedService.getCodeList('Language', lang),
        this.sharedService.getCodeList('LinkType', lang),
        this.sharedService.getCodeList('LinkDescription', lang),
      ]).subscribe(
        (res: any[]) => {
          localStorage.setItem('countries', JSON.stringify(res[0]));
          localStorage.setItem('unitCodes', JSON.stringify(res[1]));
          localStorage.setItem('licenceTypes', JSON.stringify(res[2]));
          localStorage.setItem('locationRoles', JSON.stringify(res[3]));
          localStorage.setItem('organizationRoles', JSON.stringify(res[4]));
          localStorage.setItem('organizationTypes', JSON.stringify(res[5]));
          localStorage.setItem('locationTypes', JSON.stringify(res[6]));
          localStorage.setItem('languages', JSON.stringify(res[7]));
          localStorage.setItem('linkTypes', JSON.stringify(res[8]));
          localStorage.setItem('linkDescriptions', JSON.stringify(res[9]));
          this.sharedService.notifyCodeListRefreshed(lang);
        },
        (err) => console.error('Failed fetching code-lists for', lang, err),
      );
    });
  }
}

export function httpLoaderFactory(httpClient: HttpClient) {

  return {
    getTranslation: (lang: string) => {
      return httpClient.get(
        environment.gs1ActivateUrl + '/api/v3/translation/translationByLanguage?language=' + lang
      );
    }
  };
}

export function translateCacheFactory(translateService: TranslateService, translateCacheSettings: TranslateCacheSettings) {
  return new TranslateCacheService(translateService, translateCacheSettings);
}
